import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { API_BASE_URL } from '../api';
import { JOBS_FILTER_NEW_ONLINE } from '../utils/onlineBooking';
import { prefetchJobsList } from '../utils/jobsListCache';
import { APP_VERSION, DRAWER_COLLAPSED, DRAWER_EXPANDED } from '../components/AppFooter';

import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Typography,
  Divider,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  Badge,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  CalendarMonth as CalendarIcon,
  LocalShipping as LocalShippingIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AdminPanelSettings as AdminIcon,
  Upload as UploadIcon,
  History as HistoryIcon,
  Campaign as CampaignIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Layers as LayersIcon,
  EmailOutlined as EmailOutlinedIcon,
  Bolt as BoltIcon,
  Autorenew as AutorenewIcon,
} from '@mui/icons-material';

const drawerWidth = DRAWER_EXPANDED;
const collapsedWidth = DRAWER_COLLAPSED;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/', color: '#1877F2', roles: ['super_admin', 'admin', 'office_admin', 'manager', 'user'] },
  { text: 'Jobs', icon: <AssignmentIcon />, path: '/jobs', color: '#1877F2', roles: ['super_admin', 'admin', 'manager', 'office_admin'] },
  { text: 'Calendar', icon: <CalendarIcon />, path: '/calendar', color: '#F02849', roles: ['super_admin', 'admin', 'office_admin'] },
  { text: 'Collections', icon: <ReceiptIcon />, path: '/collections', color: '#1877F2', roles: ['super_admin', 'admin', 'office_admin', 'manager', 'user', 'driver'] },
  { text: 'Estimates', icon: <ReceiptIcon />, path: '/estimates', color: '#1877F2', roles: ['super_admin', 'admin', 'office_admin', 'manager', 'user'] },
  { text: 'Price book', icon: <ReceiptIcon />, path: '/price-book', color: '#1877F2', roles: ['super_admin', 'admin', 'office_admin', 'manager', 'user'] },
  { text: 'Inventory', icon: <LocalShippingIcon />, path: '/inventory', color: '#8B5CF6', roles: ['super_admin', 'admin', 'office_admin', 'manager'] },
  { text: 'Estimate templates', icon: <LayersIcon />, path: '/estimate-templates', color: '#1877F2', roles: ['super_admin', 'admin', 'office_admin', 'manager', 'user'] },
  { text: 'Message templates', icon: <EmailOutlinedIcon />, path: '/message-templates', color: '#1877F2', roles: ['super_admin', 'admin', 'office_admin', 'manager', 'user'] },
  { text: 'Automations', icon: <BoltIcon />, path: '/automations', color: '#F7B928', roles: ['super_admin', 'admin'] },
  { text: 'Service plans', icon: <AutorenewIcon />, path: '/service-plans', color: '#06b6d4', roles: ['super_admin', 'admin', 'office_admin'] },
  { text: 'Clients', icon: <PeopleIcon />, path: '/clients', color: '#45BD62', roles: ['super_admin', 'admin', 'office_admin'] },
  { text: 'Leads', icon: <PeopleIcon />, path: '/leads', color: '#EC4899', roles: ['super_admin', 'admin', 'office_admin', 'manager'] },
  { text: 'Drivers', icon: <LocalShippingIcon />, path: '/fleet', color: '#F7B928', roles: ['super_admin', 'admin', 'office_admin'] },
  { text: 'Expenses', icon: <ReceiptIcon />, path: '/expenses', color: '#F02849', roles: ['super_admin', 'admin'] },
  { text: 'Invoices', icon: <ReceiptIcon />, path: '/invoices', color: '#1877F2', roles: ['super_admin', 'admin', 'office_admin'] },
  { text: 'Reports', icon: <BarChartIcon />, path: '/reports', color: '#45BD62', roles: ['super_admin', 'admin'] },
  { text: 'Driver Portal', icon: <LocalShippingIcon />, path: '/drivers/portal', color: '#F7B928', roles: ['super_admin', 'admin', 'manager', 'user'] },
  { text: 'Users', icon: <AdminIcon />, path: '/users', color: '#1877F2', roles: ['super_admin', 'admin'] },
  { text: 'Audit log', icon: <HistoryIcon />, path: '/audit-log', color: '#64748B', roles: ['super_admin', 'admin'] },
  { text: 'Message log', icon: <ChatBubbleOutlineIcon />, path: '/communication-log', color: '#64748B', roles: ['super_admin', 'admin', 'office_admin'] },
  { text: 'Google Ads CSV', icon: <CampaignIcon />, path: '/google-ads-exports', color: '#0EA5E9', roles: ['super_admin', 'admin'] },
];

export default function Sidebar({
  open,
  onToggle,
  onToggleTheme,
  variant = 'permanent',
  mobileOpen = false,
  onMobileClose,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  // Get real user data from localStorage
  const [user, setUser] = useState(null);

  // Profile Settings state
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState('');
  const [profilePictureLoading, setProfilePictureLoading] = useState(false);
  const [profilePictureSuccess, setProfilePictureSuccess] = useState(false);
  const [profilePictureError, setProfilePictureError] = useState('');
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [newOnlineBookingCount, setNewOnlineBookingCount] = useState(0);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        // Initialize profile data
        setProfileData(prev => ({
          ...prev,
          name: parsedUser.name || '',
          email: parsedUser.email || '',
        }));
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // Fallback user data if none exists
  const currentUser = user || {
    name: 'User',
    role: 'user',
  };

  const currentRoleName =
    (currentUser && typeof currentUser.role === 'object' && currentUser.role && currentUser.role.name) ||
    currentUser.role ||
    'user';

  const jobsMenuRoles = ['super_admin', 'admin', 'manager', 'office_admin'];
  const showNewOnlineBadge = jobsMenuRoles.includes(currentRoleName);

  useEffect(() => {
    if (!showNewOnlineBadge) {
      setNewOnlineBookingCount(0);
      return undefined;
    }

    let cancelled = false;

    const loadCount = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const qs = new URLSearchParams({
          lead_source: 'online_booking',
          status: 'pending',
          per_page: '1',
          page: '1',
        });
        const res = await fetch(`${API_BASE_URL}collections?${qs.toString()}`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const total = Number(data?.meta?.total ?? data?.total ?? 0);
        if (!cancelled) setNewOnlineBookingCount(Number.isFinite(total) ? total : 0);
      } catch {
        if (!cancelled) setNewOnlineBookingCount(0);
      }
    };

    loadCount();
    const interval = setInterval(loadCount, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [showNewOnlineBadge]);

  // Ensure name exists if user object is present but empty
  if (!currentUser.name) {
    currentUser.name = 'User';
  }
  const backendBaseUrl = API_BASE_URL.replace(/\/api\/?$/, '');
  const resolveBackendUrl = (url) => (url && !url.startsWith('http') ? `${backendBaseUrl}${url}` : (url || ''));

  // Generate avatar from user name
  const userAvatar = currentUser.name ? currentUser.name.charAt(0).toUpperCase() : 'U';
  const timezone =
    localStorage.getItem('appTimezone') ||
    currentUser?.ui_preferences?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    'UTC';
  const basedLabel = (() => {
    const tz = String(timezone || '').trim();
    if (!tz) return null;
    if (tz === 'UTC') return 'Based in UTC';
    // Most timezones are Region/City
    const parts = tz.split('/');
    const city = parts[parts.length - 1]?.replace(/_/g, ' ') || tz;
    return `Based in ${city}`;
  })();

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    // Clear localStorage and redirect to login
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Profile Settings functions
  const handleProfilePictureChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProfilePicture(file);
      setProfilePicturePreview(URL.createObjectURL(file));
      setProfilePictureError('');
    }
  };

  const handleProfilePictureUpload = async () => {
    if (!profilePicture) return;

    setProfilePictureLoading(true);
    setProfilePictureError('');

    try {
      const token = localStorage.getItem('token');
      console.log('Token from localStorage:', token);

      if (!token) {
        setProfilePictureError('No authentication token found. Please login again.');
        return;
      }

      const formData = new FormData();
      formData.append('profile_picture', profilePicture);

      console.log('Sending request to:', `${API_BASE_URL}profile/picture`);

      const response = await fetch(`${API_BASE_URL}profile/picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setProfilePictureSuccess(true);
        setProfilePicture(null);
        setProfilePicturePreview('');

        // Update user data in localStorage
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const updatedUser = { ...currentUser, profile_picture: data.profile_picture_url };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);

        setTimeout(() => setProfilePictureSuccess(false), 3000);
      } else {
        const ct = response.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const errorData = await response.json();
          const msg = errorData.message || 'Failed to upload profile picture';
          const details = errorData.errors ? Object.values(errorData.errors).flat().join('; ') : '';
          setProfilePictureError(details ? `${msg}: ${details}` : msg);
        } else {
          const errorText = await response.text();
          setProfilePictureError(`Server error: ${response.status} - ${errorText}`);
        }
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setProfilePictureError('Network error. Please try again.');
    } finally {
      setProfilePictureLoading(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        setProfilePictureError('No authentication token found. Please login again.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}profile/picture`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setProfilePictureSuccess(true);
        setProfilePicturePreview('');

        // Update user data in localStorage
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const updatedUser = { ...currentUser, profile_picture: null };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);

        setTimeout(() => setProfilePictureSuccess(false), 3000);
      } else {
        const ct = response.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const errorData = await response.json();
          const msg = errorData.message || 'Failed to remove profile picture';
          const details = errorData.errors ? Object.values(errorData.errors).flat().join('; ') : '';
          setProfilePictureError(details ? `${msg}: ${details}` : msg);
        } else {
          const errorText = await response.text();
          setProfilePictureError(`Server error: ${response.status} - ${errorText}`);
        }
      }
    } catch (error) {
      console.error('Error removing profile picture:', error);
      setProfilePictureError('Network error. Please try again.');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();

    if (profileData.new_password !== profileData.confirm_password) {
      setProfileError('New passwords do not match');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setProfileError('Authentication required');
        return;
      }

      const response = await fetch(`${API_BASE_URL}profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify(profileData)
      });

      if (response.ok) {
        // const data = await response.json(); // Unused
        setProfileSuccess(true);
        setProfileError('');

        // Update user data in localStorage
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const updatedUser = { ...currentUser, name: profileData.name, email: profileData.email };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);

        setTimeout(() => setProfileSuccess(false), 3000);
      } else {
        const errorData = await response.json();
        setProfileError(errorData.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setProfileError('Network error. Please try again.');
    }
  };

  return (
    <Drawer
      variant={variant}
      open={variant === 'temporary' ? mobileOpen : undefined}
      onClose={variant === 'temporary' ? onMobileClose : undefined}
      sx={{
        width: variant === 'temporary' ? drawerWidth : open ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: variant === 'temporary' ? drawerWidth : open ? drawerWidth : collapsedWidth,
          backgroundColor: theme.palette.mode === 'dark' ? '#141a21' : '#ffffff',
          borderRight: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
          boxShadow: 'none',
          transition: 'width 0.2s ease',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'space-between' : 'center',
          padding: open ? '14px 16px 12px' : '14px 8px 12px',
          mb: 0,
          minHeight: 56,
          borderBottom: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,23,42,0.06)',
        }}
      >
        {open && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '6px',
                background: '#4F46E5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '0.95rem',
              }}
            >
              W
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2, fontSize: '0.9375rem' }}>
                WorkWiseSoft
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.6875rem', fontWeight: 500 }}>
                Project Manager
              </Typography>
            </Box>
          </Box>
        )}
        {!open && (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #38bdf8, #2563eb)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              fontWeight: 900,
              fontSize: '18px',
            }}
          >
            W
          </Box>
        )}
      </Box>

      {/* User Profile */}
      <Box sx={{ px: open ? 1.5 : 1, mb: 2, pt: 1.5 }}>
        <Box
          sx={{
            padding: open ? '10px 12px' : '8px',
            borderRadius: '8px',
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(15, 23, 42, 0.04)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: open ? 'flex-start' : 'center',
            cursor: 'pointer',
            border: '1px solid transparent',
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            }
          }}
          onClick={() => setShowProfileSettings(true)}
        >
          <Avatar
            sx={{
              width: open ? 36 : 32,
              height: open ? 36 : 32,
              background: '#4F46E5',
              color: '#ffffff',
              fontWeight: 600,
              fontSize: open ? '0.875rem' : '0.8125rem',
              border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(15,23,42,0.08)',
            }}
          >
            {user?.profile_picture ? (
              <img src={resolveBackendUrl(user.profile_picture)} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              userAvatar
            )}
          </Avatar>

          {open && (
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.text.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {currentUser.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#10b981', boxShadow: '0 0 8px #10b981' }} />
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  {basedLabel || '—'}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Navigation Menu */}
      <List
        sx={{
          px: 1,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          py: 0.5,
        }}
      >
        {menuItems
          .filter(item => !item.roles || item.roles.includes(currentRoleName))
          .map((item) => {
            const isActive = location.pathname === item.path;
            const isJobs = item.path === '/jobs';
            const jobsBadgeCount = isJobs && showNewOnlineBadge ? newOnlineBookingCount : 0;

            return (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  onClick={() => {
                    if (item.path === '/jobs') {
                      void prefetchJobsList();
                    }
                    if (isJobs && jobsBadgeCount > 0) {
                      navigate('/jobs', { state: { [JOBS_FILTER_NEW_ONLINE]: true } });
                    } else {
                      handleNavigation(item.path);
                    }
                  }}
                  sx={{
                    borderRadius: '6px',
                    mb: 0.25,
                    minHeight: 38,
                    padding: open ? '6px 10px' : '6px',
                    justifyContent: open ? 'flex-start' : 'center',
                    gap: open ? 1.25 : 0,
                    background: isActive
                      ? (theme.palette.mode === 'dark' ? 'rgba(79, 70, 229, 0.22)' : 'rgba(79, 70, 229, 0.1)')
                      : 'transparent',
                    color: isActive
                      ? (theme.palette.mode === 'dark' ? '#c7d2fe' : '#4338CA')
                      : theme.palette.text.secondary,
                    borderLeft: isActive ? '3px solid #4F46E5' : '3px solid transparent',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(79, 70, 229, 0.06)',
                      color: theme.palette.text.primary,
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.text.primary,
                      }
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'inherit',
                      minWidth: open ? 36 : '100%',
                      marginRight: open ? 1 : 0,
                      display: 'flex',
                      justifyContent: open ? 'flex-start' : 'center',
                      '& svg': { fontSize: 20 },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    <Badge
                      badgeContent={jobsBadgeCount}
                      color="secondary"
                      max={99}
                      invisible={jobsBadgeCount === 0}
                      sx={{
                        '& .MuiBadge-badge': {
                          fontWeight: 800,
                          fontSize: '0.65rem',
                          minWidth: 18,
                          height: 18,
                        },
                      }}
                    >
                      {item.icon}
                    </Badge>
                  </ListItemIcon>

                  {open && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 600 : 500,
                        fontSize: '0.8125rem',
                        color: 'inherit',
                      }}
                      sx={{ m: 0 }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
      </List>

      {/* Logout / Toggle Section */}
      <Box
        sx={{
          flexShrink: 0,
          padding: open ? '12px 12px 16px' : '12px 8px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          borderTop: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,23,42,0.06)',
        }}
      >
        <Tooltip title="Settings" placement="right" disableHoverListener={open}>
          <Button
            fullWidth
            variant="text"
            onClick={() => handleNavigation('/settings')}
            sx={{
              color:
                location.pathname === '/settings'
                  ? theme.palette.mode === 'dark'
                    ? '#c7d2fe'
                    : '#4338CA'
                  : theme.palette.text.secondary,
              justifyContent: open ? 'flex-start' : 'center',
              paddingLeft: open ? 3 : 0,
              textTransform: 'none',
              fontWeight: location.pathname === '/settings' ? 700 : 600,
              backgroundColor:
                location.pathname === '/settings'
                  ? theme.palette.mode === 'dark'
                    ? 'rgba(79, 70, 229, 0.22)'
                    : 'rgba(79, 70, 229, 0.1)'
                  : 'transparent',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.text.primary,
              },
            }}
          >
            {open ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <SettingsIcon sx={{ fontSize: 20 }} />
                <Typography sx={{ fontWeight: 600 }}>Settings</Typography>
              </Box>
            ) : (
              <SettingsIcon sx={{ fontSize: 22 }} />
            )}
          </Button>
        </Tooltip>

        {/* Theme Toggle Button */}
        <Button
          fullWidth
          variant="text"
          onClick={() => {
            if (onToggleTheme) onToggleTheme();
          }}
          sx={{
            color: theme.palette.text.secondary,
            justifyContent: open ? 'flex-start' : 'center',
            paddingLeft: open ? 3 : 0,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.primary,
            },
          }}
        >
          {open ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ fontSize: '18px' }}>{theme.palette.mode === 'dark' ? '☀️' : '🌙'}</Box>
              <Typography sx={{ fontWeight: 600 }}>
                {theme.palette.mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ fontSize: '18px' }}>{theme.palette.mode === 'dark' ? '☀️' : '🌙'}</Box>
          )}
        </Button>

        <IconButton
          onClick={onToggle}
          sx={{
            alignSelf: open ? 'flex-end' : 'center',
            color: theme.palette.text.secondary,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '10px',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.primary,
            },
          }}
        >
          {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>

        <Button
          fullWidth
          variant="outlined"
          onClick={handleLogout}
          sx={{
            color: theme.palette.text.secondary,
            borderColor: theme.palette.divider,
            borderRadius: '12px',
            py: 1.5,
            justifyContent: open ? 'flex-start' : 'center',
            paddingLeft: open ? 3 : 0,
            '&:hover': {
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              borderColor: '#ef4444',
              color: '#ef4444',
            },
            transition: 'all 0.2s',
          }}
        >
          {open ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ fontSize: '18px' }}>🚪</Box>
              <Typography sx={{ fontWeight: 600 }}>Sign Out</Typography>
            </Box>
          ) : (
            <Box sx={{ fontSize: '18px' }}>🚪</Box>
          )}
        </Button>
      </Box>

      {/* Footer */}
      {open && (
        <Box sx={{ px: 3, pb: 2, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.7rem' }}>
            {`v${APP_VERSION} • WorkWiseSoft`}
          </Typography>
        </Box>
      )}

      {/* Profile Settings Dialog */}
      <Dialog
        open={showProfileSettings}
        onClose={() => setShowProfileSettings(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Profile Settings</DialogTitle>
        <DialogContent>
          {/* Profile Picture Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Profile Picture
            </Typography>

            {/* Current Profile Picture */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '24px',
                }}
              >
                {user?.profile_picture ? (
                  <img src={resolveBackendUrl(user.profile_picture)} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  currentUser.name ? currentUser.name.charAt(0).toUpperCase() : 'U'
                )}
              </Avatar>

              <Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="profile-picture-input"
                  type="file"
                  onChange={handleProfilePictureChange}
                />
                <label htmlFor="profile-picture-input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    sx={{ mb: 1, display: 'block' }}
                  >
                    CHOOSE PHOTO
                  </Button>
                </label>

                {user?.profile_picture && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleRemoveProfilePicture}
                    sx={{ mb: 1, display: 'block' }}
                  >
                    REMOVE PHOTO
                  </Button>
                )}
              </Box>
            </Box>

            {/* Profile Picture Preview */}
            {profilePicturePreview && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>Preview:</Typography>
                <img
                  src={profilePicturePreview}
                  alt="Preview"
                  style={{
                    width: 100,
                    height: 100,
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '2px solid #e2e8f0'
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleProfilePictureUpload}
                  disabled={profilePictureLoading}
                  sx={{ ml: 2 }}
                >
                  {profilePictureLoading ? 'Uploading...' : 'Upload Picture'}
                </Button>
              </Box>
            )}

            {/* Messages */}
            {profilePictureSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Profile picture updated successfully!
              </Alert>
            )}
            {profilePictureError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {profilePictureError}
              </Alert>
            )}
            {profilePictureLoading && (
              <LinearProgress sx={{ mb: 2 }} />
            )}
          </Box>

          {/* Profile Information Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Profile Information
            </Typography>

            <form onSubmit={handleProfileUpdate}>
              <TextField
                fullWidth
                label="Name"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                margin="normal"
                required
              />

              <TextField
                fullWidth
                label="Email"
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                margin="normal"
                required
              />

              <Button
                type="submit"
                variant="contained"
                sx={{ mt: 2 }}
              >
                Update Profile
              </Button>
            </form>

            {profileSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Profile updated successfully!
              </Alert>
            )}
            {profileError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {profileError}
              </Alert>
            )}
          </Box>

          {/* Change Password Section */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Change Password
            </Typography>

            <form onSubmit={handleProfileUpdate}>
              <TextField
                fullWidth
                label="Current Password"
                type="password"
                value={profileData.current_password}
                onChange={(e) => setProfileData({ ...profileData, current_password: e.target.value })}
                margin="normal"
              />

              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={profileData.new_password}
                onChange={(e) => setProfileData({ ...profileData, new_password: e.target.value })}
                margin="normal"
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={profileData.confirm_password}
                onChange={(e) => setProfileData({ ...profileData, confirm_password: e.target.value })}
                margin="normal"
              />
            </form>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowProfileSettings(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}